import { useRef, useState, useEffect, useCallback, type ReactNode } from 'react';

import { Alert, Stack, Button } from '@mui/material';

import type { BusinessDeviceSyncFields } from 'src/utils/Services/device-sync-v4.service';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { AUTH_LOGOUT_REASON } from 'src/auth/store/firebase/logout-reason';
import { connectivityCoordinator } from 'src/features/connectivity/connectivity-service';
import BusinessDocumentListenerProvider from 'src/auth/guard/business-document-listener-provider';
import {
  classifyAuthOperationError,
  isTransientAuthOperationFailure,
} from 'src/auth/errors/auth-operation-error';

import { SplashScreen } from 'src/components/loading-screen';

import { usePointPaySession } from './session-context';
import { PointPayIdentityError } from './session-identity';
import {
  publishPointPayUser,
  refreshPointPayUserData,
  validatePointPayUserData,
  refreshPointPayBusinessChanges,
} from './user-data.service';

export default function PointPayUserDataGuard({ children }: { children: ReactNode }) {
  const identity = usePointPaySession();
  const business = useDeviceSyncStore((state) => state.businessData);
  const users = useDeviceSyncStore((state) => state.userData);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(false);
  const loggingOutRef = useRef(false);

  const handleFailure = useCallback((failure: unknown) => {
    if (!mountedRef.current) return;
    const normalized =
      failure instanceof Error ? failure : new Error('Unable to load your business. Please retry.');
    setError(normalized);
    const kind = classifyAuthOperationError(failure);
    if (
      failure instanceof PointPayIdentityError ||
      kind.kind === 'AUTHENTICATION' ||
      kind.kind === 'AUTHORIZATION'
    ) {
      if (!loggingOutRef.current) {
        loggingOutRef.current = true;
        useAuthStore
          .getState()
          .logout({ reason: AUTH_LOGOUT_REASON.USER_LOGOUT })
          .catch(() => {
            loggingOutRef.current = false;
          });
      }
    } else if (isTransientAuthOperationFailure(kind)) {
      connectivityCoordinator.reportStartupFailure('pointpay-preload', normalized);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!identity || !mountedRef.current) return;
    setRefreshing(true);
    try {
      await refreshPointPayUserData(identity);
      if (mountedRef.current) {
        setReady(true);
        setError(null);
      }
      connectivityCoordinator.clearStartupFailure('pointpay-preload');
    } catch (failure) {
      handleFailure(failure);
      throw failure;
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [handleFailure, identity]);

  useEffect(() => {
    mountedRef.current = true;
    const unregister = connectivityCoordinator.registerRecoveryTask('pointpay-preload', refresh);
    refresh().catch(() => undefined);
    return () => {
      mountedRef.current = false;
      unregister();
      connectivityCoordinator.clearStartupFailure('pointpay-preload');
    };
  }, [refresh]);

  // Every user/business refresh re-resolves the active user, including changes
  // initiated by Settings. Never retain an old user's role or active status.
  let invalid: Error | null = null;
  if (ready && identity) {
    try {
      validatePointPayUserData(identity, { businessData: business ?? undefined, userData: users });
    } catch (failure) {
      invalid = failure as Error;
    }
  }
  const invalidMessage = invalid?.message;
  useEffect(() => {
    if (!ready || !identity) return;
    if (invalidMessage) handleFailure(new PointPayIdentityError(invalidMessage));
    else publishPointPayUser(identity, { businessData: business ?? undefined, userData: users });
  }, [ready, identity, business, users, invalidMessage, handleFailure]);

  const onChanges = useCallback(
    async (fields: BusinessDeviceSyncFields) => {
      if (!identity || !mountedRef.current) return;
      try {
        await refreshPointPayBusinessChanges(identity, fields);
      } catch (failure) {
        handleFailure(failure);
      }
    },
    [identity, handleFailure]
  );

  if (error)
    return (
      <Stack spacing={2} sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error.message}</Alert>
        <Button disabled={refreshing} onClick={() => refresh().catch(() => undefined)}>
          {refreshing ? 'Retrying…' : 'Retry'}
        </Button>
      </Stack>
    );
  if (!ready || !identity || invalid) return <SplashScreen />;
  return (
    <BusinessDocumentListenerProvider
      enableInventoryData={false}
      enableInventorySync={false}
      enableDeviceControl={false}
      onDeviceSyncFieldsChange={onChanges}
    >
      {children}
    </BusinessDocumentListenerProvider>
  );
}
